// @flow

import _ from 'lodash';
import React, { useState, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';

import OldMenu from 'src/components/containers/main-menu-old';
import MainMenuItem from './item';
import { Popover } from '@shopify/polaris';
import { getMenuLeft, getMenuRight } from 'src/helpers/menu-helper.js';
import { allowUsingNewSearchSettings } from 'src/helpers/search-settings-helper';
import { enableShopifyIntegration, isUsingNewTheme } from 'src/helpers/theme-setup-helper';
import { ComposedProps } from './index.type';
import { ADMIN_APP_ASSET_PATHS } from 'src/constants/assets';
import { getAssetURL } from 'src/helpers';
import * as commonAction from 'src/stores/actions/common-actions';
import * as oauthAction from 'actions/oauth-actions';

//-----------------------------------------
/**
 * menuIdMetaMatchOldNav: to matching for old nav bar, format XXX___YYY
 * XXX: parent of old nav, YYY: id of child in parent menu
 * ex: menu-theme___menu-integration-shopify-integration
 * for case: new nav, parent key is menu-integration, it not match parent menu-them in old nav
 *  menuId="menu-integration-theme-setup"
 *  menuIdMetaMatchOldNav="menu-theme___menu-integration-shopify-integration"
 *
 * @param {*} props
 * @returns
 */
const MainMenu = (props: ComposedProps) => {
  const {
    i18n,
    usingNewTheme = false,
    menuId = '',
    menuIdMetaMatchOldNav,
    isEnableShopifyIntegration,
    enabledRecommendationType,
    enableAISearch,
    isNewSearch,
    tenantApp,
    enabledSubscriptionV5,
    skipTourABTesting,
    updateFeaturesAppSettings,
    useBoltOn
  } = props;

  const [popoverActiveMenuMini, setPopoverActionMenuMini] = useState(false);
  const togglePopoverActive = useCallback(() => {
    setPopoverActionMenuMini(prevState => !prevState);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', () => setPopoverActionMenuMini(false));
  }, []);

  if (!isNewSearch)
    return <OldMenu menuId={menuId} menuIdMetaMatchOldNav={menuIdMetaMatchOldNav} />;
  const menuLeft = getMenuLeft(
    i18n,
    {
      hideAISearch: !isNewSearch,
      usingNewTheme,
      hasShopifyIntegration: isEnableShopifyIntegration,
      enabledSubscriptionV5
    },
    tenantApp
  );
  const menuRight = getMenuRight(i18n, {
    hasShopifyIntegration: isEnableShopifyIntegration,
    useBoltOn,
    enabledRecommendationType,
    enableAISearch,
    enabledSubscriptionV5
  });

  const activatorMiniMenu = (
    <span className="action" onClick={togglePopoverActive}>
      <img
        src={
          popoverActiveMenuMini
            ? getAssetURL(ADMIN_APP_ASSET_PATHS?.common?.menuClose)
            : getAssetURL(ADMIN_APP_ASSET_PATHS?.common?.menu)
        }
        width={20}
        height={20}
        alt="menu"
      />
    </span>
  );

  return (
    <React.Fragment>
      <div className="PFS_MainMenu_Wrapper">
        <div className="PFS_MainMenu">
          <div className="PFS_MainMenu--Mini">
            <div className="left">
              <Popover
                active={popoverActiveMenuMini}
                activator={activatorMiniMenu}
                onClose={() => {}}
                preferredAlignment="left">
                {menuLeft.map(menuItem => (
                  <MainMenuItem
                    type="mobile"
                    key={menuItem.id}
                    menuId={menuId}
                    tenantApp={tenantApp}
                    menuIdMetaMatchOldNav={menuIdMetaMatchOldNav}
                    data={menuItem}
                    updateFeaturesAppSettings={updateFeaturesAppSettings}
                    skipTourABTesting={skipTourABTesting}
                    position="left"
                    className="menu-mobile"
                  />
                ))}
              </Popover>
            </div>
            <div className="right">
              {menuRight
                .filter(e => !e.isHide)
                .map(menuItem => (
                  <MainMenuItem
                    key={menuItem.id}
                    type="mobile"
                    menuId={menuId}
                    menuIdMetaMatchOldNav={menuIdMetaMatchOldNav}
                    data={menuItem}
                    position="right"
                    tenantApp={tenantApp}
                    updateFeaturesAppSettings={updateFeaturesAppSettings}
                    skipTourABTesting={skipTourABTesting}
                  />
                ))}
            </div>
          </div>
        </div>
        <div className="PFS_MainMenu">
          <div className="menu-features">
            {menuLeft.map(menuItem => (
              <MainMenuItem
                key={menuItem.id}
                type="web"
                menuId={menuId}
                menuIdMetaMatchOldNav={menuIdMetaMatchOldNav}
                tenantApp={tenantApp}
                data={menuItem}
                updateFeaturesAppSettings={updateFeaturesAppSettings}
                skipTourABTesting={skipTourABTesting}
                position="left"
              />
            ))}
          </div>
          <div className="menu-settings">
            {menuRight
              .filter(e => !e.isHide)
              .map(menuItem => (
                <MainMenuItem
                  key={menuItem.id}
                  type="web"
                  menuId={menuId}
                  menuIdMetaMatchOldNav={menuIdMetaMatchOldNav}
                  tenantApp={tenantApp}
                  data={menuItem}
                  updateFeaturesAppSettings={updateFeaturesAppSettings}
                  skipTourABTesting={skipTourABTesting}
                  position="right"
                />
              ))}
          </div>
        </div>
      </div>

    </React.Fragment>
  );
};

const mapStateToProps = state => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const tenantAppSettings = _.get(state.oauth, 'tenantApp.settings', {});
  const useBoltOn = _.get(state.oauth, 'subscription.useBoltOn', false);
  const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);
  const searchVersion = _.get(state.oauth, 'tenantApp.parseParams.searchVersion', '1.0');
  const isNewSearch = allowUsingNewSearchSettings(searchVersion);
  const enabledRecommendationType = _.get(
    tenantApp,
    `settings.recommendation.settings.frequently-bought-together`,
    false
  );
  const enableAISearch = _.get(
    state.oauth.tenantApp,
    'settings.instantSearch.settings.AI-synonyms',
    false
  );

  return {
    tenantApp,
    isEnableShopifyIntegration,
    tenantAppSettings,
    usingNewTheme: isUsingNewTheme(tenantApp),
    useBoltOn,
    enabledRecommendationType,
    enableAISearch,
    isNewSearch,
    enabledSubscriptionV5: _.get(tenantApp, 'settings.subscriptionV5.settings.enable', false)
  };
};

const mapDispatchToProps = dispatch => ({
  skipTourABTesting: (meta, i18n) =>
    dispatch(commonAction.handleSkipTourABTestingGuide(meta, i18n)),
  updateFeaturesAppSettings: (data, featureName) =>
    dispatch(oauthAction.updateFeaturesAppSettings(data, featureName))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(MainMenu));
